/**
 * Bulk reclassify persistence (FR-CAT-09, FR-CAT-10).
 *
 * The plan is decided in `core/reclassify.ts`; this writes it.
 *
 * Three things this file is careful about:
 *
 *  · **Only the four reclassify fields are written.** Not the whole document. A patch
 *    cannot move an amount, a date or an account, so no account balance can drift out of
 *    step with the ledger from this screen — by construction rather than by review. The
 *    guard below turns that from a convention into an enforced one.
 *
 *  · **Rollup deltas are aggregated per month across the chunk.** 180 rows in three months
 *    is three rollup writes, not 180 — and Firestore rejects two writes to one document
 *    in a single batch, so per-transaction rollup updates would not even be legal.
 *
 *  · **One audit entry per changed transaction, in the same batch** (FR-CAT-10).
 */

import { doc, increment, serverTimestamp, writeBatch } from 'firebase/firestore';
import { diffFields } from '../core/audit';
import { RECLASSIFY_FIELDS, type PlannedChange, type ReclassifyPlan } from '../core/reclassify';
import { editDeltas, nestFields, type AccountTypeLookup } from '../core/rollups';
import { stageAudit } from './audit';
import { db } from './firebase';
import { trackWrite } from './syncState';

export class ReclassifyError extends Error {}

/**
 * Transactions per batch.
 *
 * Each row costs two writes — the update and its audit entry — plus a handful of rollup
 * documents for the chunk. 100 rows is ~210 operations, comfortably inside Firestore's
 * 500 limit even when a chunk straddles several months.
 */
const CHUNK = 100;

export interface ReclassifyProgress {
  written: number;
  total: number;
}

const WRITABLE = new Set<string>(RECLASSIFY_FIELDS);

/**
 * Apply the plan.
 *
 * Returns the number of transactions changed — which is `plan.changes.length`, but coming
 * back from the writer rather than the planner means the figure reported to the user is
 * the one that was actually committed.
 */
export async function applyReclassification(
  plan: ReclassifyPlan,
  by: string,
  accountType: AccountTypeLookup,
  onProgress?: (progress: ReclassifyProgress) => void,
): Promise<number> {
  const rows = plan.changes;
  if (rows.length === 0) return 0;

  for (const row of rows) guard(row);

  for (let i = 0; i < rows.length; i += CHUNK) {
    const slice = rows.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    const rollupsByMonth = new Map<string, Record<string, number>>();

    for (const row of slice) {
      batch.update(doc(db, 'transactions', row.before.id), {
        ...row.patch,
        updatedAt: serverTimestamp(),
      });

      // Reclassify never moves a date, so before and after share a month and this is
      // always a single entry — but going through editDeltas rather than assuming that
      // keeps one delta implementation for every kind of edit.
      for (const [monthKey, fields] of editDeltas(row.before, row.after, accountType)) {
        const merged = rollupsByMonth.get(monthKey) ?? {};
        for (const [path, value] of Object.entries(fields)) {
          merged[path] = (merged[path] ?? 0) + value;
        }
        rollupsByMonth.set(monthKey, merged);
      }

      stageAudit(batch, {
        action: 'update',
        collection: 'transactions',
        documentId: row.before.id,
        changes: diffFields(
          pick(row.before as unknown as Record<string, unknown>, row.fields),
          pick(row.after as unknown as Record<string, unknown>, row.fields),
        ),
        by,
        context: 'bulk-reclassify',
      });
    }

    for (const [monthKey, fields] of rollupsByMonth) {
      // Cancelled paths carry no information and would only widen the write. Two rows
      // swapped between the same pair of categories in one batch is exactly this case.
      const live = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== 0));
      if (Object.keys(live).length === 0) continue;

      batch.set(
        doc(db, 'rollups', monthKey),
        { monthKey, updatedAt: serverTimestamp(), ...nestFields(live, increment) },
        { merge: true },
      );
    }

    await trackWrite(batch.commit());
    onProgress?.({ written: Math.min(i + CHUNK, rows.length), total: rows.length });
  }

  return rows.length;
}

/**
 * Refuse to write anything this screen has no business writing.
 *
 * Cheap, and it means a future change to the plan shape cannot quietly turn bulk
 * reclassify into a path that edits amounts or accounts.
 */
function guard(row: PlannedChange): void {
  for (const field of Object.keys(row.patch)) {
    if (!WRITABLE.has(field)) {
      throw new ReclassifyError(`Bulk reclassify cannot write "${field}".`);
    }
  }
  if (
    row.after.accountId !== row.before.accountId ||
    row.after.toAccountId !== row.before.toAccountId ||
    row.after.amountPaise !== row.before.amountPaise ||
    row.after.amountInrPaise !== row.before.amountInrPaise ||
    row.after.type !== row.before.type ||
    row.after.dateKey !== row.before.dateKey
  ) {
    throw new ReclassifyError(
      'Bulk reclassify would move a balance. Refusing to write — edit that transaction on its own.',
    );
  }
}

function pick(source: Record<string, unknown>, fields: readonly string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const field of fields) result[field] = source[field];
  return result;
}
